import { lazy, Suspense } from 'react'
import classNames from 'classnames'
import merge from 'deepmerge'
import { observer } from 'mobx-react'
import { array, number, object, oneOfType, string } from 'prop-types'

import { countries, numberUtils } from 'utils'

import defaultOptions from './options'
import './index.scss'

const Chart = lazy(() => import(/* webpackChunkName: "react-apexcharts" */ 'react-apexcharts'))
const MARGIN = 30

const CountryGraph = observer(
  ({ countries: countriesList, width, height, options, maxCount, maxValue, className, ...props }) => {
    const sliced = countriesList.slice(0, maxCount)
    const series = [{ data: sliced.map(v => Number(v.percent.toFixed(4))) }]
    const h = ((height - MARGIN) / maxCount) * sliced.length + MARGIN

    const slicedAlpha3Names = sliced.map(v => countries.byValue(v.country).ALPHA_3)

    const ops = {
      xaxis: {
        categories: slicedAlpha3Names,
      },
      yaxis: {
        min: 0,
        max: maxValue ? maxValue : Number((Math.max(...sliced.map(v => v.percent)) * 1.2).toFixed(2)),
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex }) => {
          return `<div class="CountryGraph-customTooltip">
                    <div class="CountryGraph-customTooltip-country">
                      ${countries.byValue(sliced[dataPointIndex].country).NAME}
                    </div>
                    <div class="CountryGraph-customTooltip-series-group">
                      <div>
                        <div class="CountryGraph-customTooltip-percent">
                          <span class="CountryGraph-customTooltip-percent-label">Percent: </span>
                          <span class="CountryGraph-customTooltip-percent-value">
                            ${numberUtils.formatPercent(series[seriesIndex][dataPointIndex], 1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>`
        },
        marker: {
          show: false,
        },
      },
      chart: {
        toolbar: {
          tools: {
            download: false,
          },
        },
        animations: {
          enabled: false,
        },
      },
    }
    return (
      <Suspense fallback={null}>
        <Chart
          type="bar"
          height={h}
          width={width}
          series={series}
          options={merge.all([defaultOptions, options, ops])}
          className={classNames('country-graph', className)}
          {...props}
        />
      </Suspense>
    )
  }
)

CountryGraph.propTypes = {
  countries: array.isRequired,
  options: object,
  width: oneOfType([number, string]),
  height: oneOfType([number, string]),
  maxCount: number.isRequired,
  maxValue: number,
}

CountryGraph.defaultProps = {
  height: 360,
  width: '100%',
  options: {},
  maxCount: 6,
  countries: [],
}

CountryGraph.displayName = 'CountryGraph'

export default CountryGraph
